import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { NotificationsService } from 'angular2-notifications';
import { ManageAgreementService } from '../manage-agreement/manage-agreement.service';
import { BoletoAcertoDto } from '../models/boleto-acerto.model';
import * as momentImport from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { CreateCollectionExternalAgreementDto } from '../models/create-collectionExternal-agreement.model';
import { AuthServiceCustom } from 'src/app/core/auth/authServiceCustom';

const moment: any = (momentImport as any).default || momentImport;

export const MY_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM yyyy',
        dateA11yLabel: 'DD',
        monthYearA11yLabel: 'MMMM yyyy',
    },
};

interface CompanyCollector {
    companyId: string;
    name: string;
}

@Component({
    selector: 'create-agreement',
    templateUrl: './create-agreement.component.html',
    styleUrls: ['./create-agreement.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
})
export class CreateAgreementComponent implements OnInit {
    filterForm: FormGroup;
    viewOnlySelected = false;
    boletos: BoletoAcertoDto[] = [];
    filteredBoletos: BoletoAcertoDto[] = [];
    displayedBoletos: BoletoAcertoDto[] = [];
    selectedBoletos: BoletoAcertoDto[] = [];
    totalSelecionado = 0;
    companies: CompanyCollector[] = [];
    pageIndex = 0;
    totalItems = 0;
    pageSizeOptions = [20, 50, 100, 200, 500];
    pageSize = this.pageSizeOptions[0];
    sortField:
        | 'numeroCarne'
        | 'nomeAluno'
        | 'nomeEscola'
        | 'description'
        | 'dataVencimento'
        | 'dataPagamento'
        | 'numeroRecibo'
        | 'valorPago'
        | null = null;
    sortDirection: 'asc' | 'desc' = 'asc';
    tableFilter = '';
    loading: boolean = true;

    constructor(
        private _notification: NotificationsService,
        private _manageAgreementService: ManageAgreementService,
        private spinner: NgxSpinnerService,
        private _cdRef: ChangeDetectorRef,
        private _authService: AuthServiceCustom,
    ) {
        this.filterForm = new FormGroup({
            startDate: new FormControl(moment().subtract(30, 'days')),
            endDate: new FormControl(moment()),
            companyId: new FormControl(null),
            receiptCode: new FormControl(''),
            isInternalReceipt: new FormControl('1'), // 0 para recebimento externo, 1 para interno
            filterDateName: new FormControl('duedateFilter'),
        });
    }

    showLoad(value: boolean) {
        this.loading = value;
        if (this.loading) {
            this.spinner.show();
        } else {
            this.spinner.hide();
        }
        this._cdRef.detectChanges();
    }

    ngOnInit(): void {
        this.loadCompanies();
    }

    loadCompanies(): void {
        this.showLoad(true);
        this._manageAgreementService.loadCompanies().subscribe(
            (result) => {
                this.showLoad(false);
                if (result?.success && Array.isArray(result.data)) {
                    this.companies = result.data.map((company: any) => ({
                        companyId: company.companyId,
                        name: company.name,
                    }));
                    if (this.companies.length) {
                        this.filterForm.get('companyId')?.setValue(this.companies[0].companyId);
                    }
                } else {
                    this._notification.error('Erro', 'Não foi possível carregar as empresas');
                }
            },
            () => {
                this._notification.error('Erro', 'Não foi possível carregar as empresas');
                this.showLoad(false);
            },
        );
    }

    buscar(): void {
        this.pageIndex = 0;
        this.loadBillets();
    }

    loadBillets(): void {
        const companyId = this.filterForm.value.companyId;
        const startDate = this.filterForm.value.startDate;
        const endDate = this.filterForm.value.endDate;

        if (!companyId) {
            this._notification.error('Erro', 'Selecione uma empresa antes de buscar');
            return;
        }

        if (!startDate || !endDate) {
            this._notification.error('Erro', 'Informe a data inicial e final para buscar boletos');
            return;
        }

        const start = moment(startDate).startOf('day').toISOString();
        const end = moment(endDate).endOf('day').toISOString();

        this.showLoad(true);

        this._manageAgreementService
            .getBilletsCollector(
                companyId,
                start,
                end,
                this.filterForm.value.filterDateName,
                this.filterForm.value.isInternalReceipt,
                this.filterForm.value.receiptCode?.trim(),
            )
            .subscribe(
                (result) => {
                    this.showLoad(false);
                    let payload: BoletoAcertoDto[] = result ?? [];
                    if (Array.isArray(payload)) {
                        this.boletos = [];
                        this.totalItems = payload.length;

                        //all false
                        payload.forEach((item: BoletoAcertoDto) => {
                            item.selected = false;
                            this.boletos.push(item);
                        });

                        this.applyTableFilter();
                        this.updateDisplayedBoletos();
                        this.atualizarTotal();
                        this._notification.create('Sucesso', 'Boletos carregados com sucesso');
                    } else {
                        this.boletos = [];
                        this.filteredBoletos = [];
                        this.totalItems = 0;
                        this.atualizarTotal();
                        this._notification.error('Erro', 'Não foi possível carregar os boletos');
                    }
                },
                () => {
                    this.showLoad(false);
                    this.boletos = [];
                    this.totalItems = 0;
                    this.atualizarTotal();
                    this._notification.error('Erro', 'Não foi possível carregar os boletos');
                },
            );
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredBoletos.length / this.pageSize));
    }

    previousPage(): void {
        if (this.pageIndex > 0) {
            this.pageIndex--;
            this.updateDisplayedBoletos();
        }
    }

    nextPage(): void {
        if (this.pageIndex < this.totalPages - 1) {
            this.pageIndex++;
            this.updateDisplayedBoletos();
        }
    }

    setPageSize(value: string): void {
        this.pageSize = Number(value);
        this.pageIndex = 0;
        this.updateDisplayedBoletos();
    }

    onSelectBoleto(boleto: BoletoAcertoDto): void {
        boleto.selected = !boleto.selected;
        this.atualizarTotal();
    }

    updateDisplayedBoletos(): void {
        const startIndex = this.pageIndex * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedBoletos = this.filteredBoletos.slice(startIndex, endIndex);
    }

    filterOnlySelected() {
        let hasOnlySelected =
            this.filteredBoletos.some((b) => b.selected) && !this.filteredBoletos.every((b) => b.selected);

        if (hasOnlySelected) {
            this.filteredBoletos = this.boletos.filter((b) => b.selected == hasOnlySelected);
            this.viewOnlySelected = true;
        } else {
            this.filteredBoletos = [...this.boletos];
            this.viewOnlySelected = false;
        }
        this.pageIndex = 0;
        this.updateDisplayedBoletos();
    }

    applyTableFilter(): void {
        const filter = this.tableFilter?.toLowerCase().trim();
        if (!filter) {
            this.filteredBoletos = [...this.boletos];
        } else {
            this.filteredBoletos = this.boletos.filter((boleto) => {
                const searchable = [
                    boleto.numeroCarne,
                    boleto.nomeAluno,
                    boleto.nomeEscola,
                    boleto.description,
                    boleto.numeroRecibo,
                    boleto.valorPago?.toString(),
                    boleto.dataVencimento ? new Date(boleto.dataVencimento).toLocaleDateString('pt-BR') : '',
                    boleto.dataPagamento ? new Date(boleto.dataPagamento).toLocaleDateString('pt-BR') : '',
                ]
                    .filter(Boolean)
                    .join(' | ')
                    .toLowerCase();
                return searchable.includes(filter);
            });
        }

        if (this.pageIndex >= this.totalPages) {
            this.pageIndex = 0;
        }
        this.updateDisplayedBoletos();
    }

    isAllSelected(): boolean {
        return this.displayedBoletos.length > 0 && this.displayedBoletos.every((b) => b.selected);
    }

    toggleSelectAll(): void {
        const isAllSelected = this.isAllSelected();
        this.displayedBoletos.forEach((b) => (b.selected = !isAllSelected));
        this.atualizarTotal();
    }

    sortBy(
        field:
            | 'numeroCarne'
            | 'nomeAluno'
            | 'nomeEscola'
            | 'description'
            | 'dataVencimento'
            | 'dataPagamento'
            | 'numeroRecibo'
            | 'valorPago',
    ): void {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }

        this.boletos.sort((a, b) => {
            let aValue: any = a[field];
            let bValue: any = b[field];

            if (field === 'dataVencimento' || field === 'dataPagamento') {
                aValue = aValue ? new Date(aValue).getTime() : 0;
                bValue = bValue ? new Date(bValue).getTime() : 0;
            }

            if (aValue === bValue) {
                return 0;
            }

            const comparison = aValue > bValue ? 1 : -1;
            return this.sortDirection === 'asc' ? comparison : -comparison;
        });

        this.applyTableFilter();
    }

    atualizarTotal(): void {
        this.selectedBoletos = this.boletos.filter((b) => b.selected);
        this.totalSelecionado = this.selectedBoletos.reduce((sum, b) => sum + b.valorPago, 0);
    }

    confirmar(): void {
        if (this.selectedBoletos.length === 0) {
            this._notification.error('Erro', 'Selecione pelo menos um boleto');
            return;
        }

        let body: CreateCollectionExternalAgreementDto = {
            username: this._authService.userName,
            companyId: this.filterForm.value.companyId,
            billets: this.selectedBoletos.map((b) => ({
                valuePaid: b.valorPago,
                sequential: b.codigoCarne,
                isPartial: (b.description ?? '').trim().toLowerCase() === 'receb. parcial',
                receiptCode: b.numeroRecibo,
            })),
        };

        this.showLoad(true);
        this._manageAgreementService.createCollectionExternalAgreement(body).subscribe(
            (response) => {
                this.showLoad(false);
                this._notification.create('Sucesso', response ?? 'Acerto criado com sucesso');
                this.clearTable();
            },
            (error) => {
                this.showLoad(false);
                console.error('Erro ao criar acerto:', error);
            },
        );
    }

    clearTable() {
        //clear table
        this.boletos = [];
        this.filteredBoletos = [];
        this.displayedBoletos = [];
        this.selectedBoletos = [];
        this.totalItems = 0;
        this.atualizarTotal();
        this._cdRef.detectChanges();
    }
}
